import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fetchReportRepairs } from '@/lib/api/reports.js';
import { exportToCsv } from '@/lib/csv-export.js';
import { formatCurrency } from '@/lib/format.js';
import { useReportQueryParams } from '@/pages/reports/report-filters-context.js';

export function RepairsReportPage() {
  const qBuilt = useReportQueryParams();

  const q = useQuery({
    queryKey: ['reports-repairs', qBuilt],
    queryFn: () => fetchReportRepairs(qBuilt),
  });

  const rows = q.data ?? [];

  const chartData = useMemo(() => rows.slice(0, 15).map((r) => ({ name: r.serviceName.slice(0, 24), count: r.count })), [rows]);

  const csv = () => {
    exportToCsv(
      rows.map((r) => ({
        serviceName: r.serviceName,
        count: r.count,
        revenue: r.revenue,
        avgPrice: r.avgPrice,
        percentOfTotal: r.percentOfTotal,
        comebackRate: r.comebackRate,
      })),
      'repairs.csv',
    );
  };

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading repairs…</div>;
  if (q.isError) return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load repairs report.</div>;

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50" onClick={csv}>
          Export CSV
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-bold text-slate-950">Top services by volume</h3>
        <div className="mt-4 h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-600">
            <tr>
              <th className="px-4 py-3">Service</th>
              <th className="px-4 py-3">Count</th>
              <th className="px-4 py-3">Revenue</th>
              <th className="px-4 py-3">Avg price</th>
              <th className="px-4 py-3">% of total</th>
              <th className="px-4 py-3">Comeback</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.serviceName} className="border-b border-slate-100">
                <td className="px-4 py-2 font-medium">{r.serviceName}</td>
                <td className="px-4 py-2">{r.count}</td>
                <td className="px-4 py-2">{formatCurrency(r.revenue)}</td>
                <td className="px-4 py-2">{formatCurrency(r.avgPrice)}</td>
                <td className="px-4 py-2">{r.percentOfTotal}%</td>
                <td className="px-4 py-2">{r.comebackRate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length ? <p className="p-6 text-center text-slate-500">No repair lines in this period.</p> : null}
      </div>
    </div>
  );
}
